import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import FlagIcon from "@mui/icons-material/Flag";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useEffect, useRef, useState } from "react";
import { getStatusOptions, getStatusOptionsArray } from "../utils/utils";
import ListMenu from "./ListMenu";
import FlagDialog from "./FlagDialog";
import AddToCollectionDropdown from "../features/curate/AddToCollectionDropdown";
import CollectionDialog from "../features/curate/CollectionDialog";
import { useAuth } from "../context/authContext";

export default function ContentCardActions({
  status,
  onStatusChange,
  contentId,
  contentType,
  statusOptionsOverride,
  statusMenuFooterText,
  seasonNumber, // set only for an individual TV season tab (see TVShowCard) —
  // appends "S<x>" to each dropdown action so it reads e.g. "Finish S2"
  // instead of an ambiguous "Finish".
  openSignal, // bumped by a sibling component (e.g. ContentJournal's empty
  // state) to open this dropdown remotely, anchored to our own status button.
}) {
  const { user, openAuthDialog } = useAuth();
  const theme = useTheme();
  const statusButtonRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const statusOptions = getStatusOptions(theme);
  const seasonSuffix = seasonNumber ? ` S${seasonNumber}` : "";
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);

  // openSignal starts at 0/undefined, so this is a no-op on mount and only
  // fires on a genuine remote-open request — including a repeat request while
  // already open, since it's a counter rather than a boolean.
  useEffect(() => {
    if (openSignal) setStatusAnchorEl(statusButtonRef.current);
  }, [openSignal]);
  const [addToCollectionAnchorEl, setAddToCollectionAnchorEl] = useState(null);
  const [openCreateCollection, setOpenCreateCollection] = useState(false);
  const [openFlagDialog, setOpenFlagDialog] = useState(false);
  const currentStatus = statusOptions[status];
  // queued/started come first as onboarding steps; finished before abandoned
  // so "Finish" reads as the primary action ahead of "Stop"
  const statusDisplayOrder = ["queued", "started", "finished", "abandoned"];
  const baseStatusOptionsArray = getStatusOptionsArray(theme)
    .filter(
      (option) => option.value !== "none" && option.value !== status,
      // remove default "none" option as well as the current status
    )
    .sort(
      (a, b) =>
        statusDisplayOrder.indexOf(a.value) -
        statusDisplayOrder.indexOf(b.value),
    );

  const statusOptionsDropdown =
    statusOptionsOverride ??
    [
      ...(status === "finished"
        ? [
            {
              label: (
                <>
                  Finish{seasonSuffix}
                  <br />
                  again
                </>
              ),
              value: "logAgain",
              icon: statusOptions.finished.iconFilled,
              color: theme.palette.status.finished.main,
            },
          ]
        : []),
      ...baseStatusOptionsArray.map((option) => ({
        ...option,
        label: `${option.action}${seasonSuffix}`,
        icon: option.iconFilled,
      })),
      ...(status !== "none"
        ? [
            {
              label: (
                <>
                  Clear{seasonSuffix}
                  <br />
                  tracking
                </>
              ),
              value: "none",
              icon: <HighlightOffIcon />,
              color: "#bdbdbd",
            },
          ]
        : []),
    ].filter(Boolean); // needed to filter out falsey values that originate due to the conditions used in constructing the array

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusSelect = (option) => async (event) => {
    // This function needs to accept an event even though it is not used as that
    // is how the onClick prop of the ListMenu component is configured
    setStatusAnchorEl(null);
    setSubmitting(true);
    try {
      await onStatusChange(option);
    } catch (e) {
      // error already handled by query client
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddToCollection = (event) => {
    if (!user) {
      openAuthDialog("Adding to collections");
      return;
    }
    setAddToCollectionAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <Stack direction="row" spacing={1} alignItems="center">
          {user && (
            <Tooltip title="Flag">
              <IconButton
                sx={{ p: 0.5 }}
                onClick={() => setOpenFlagDialog(true)}
              >
                <FlagIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Add to collection">
            <IconButton sx={{ p: 0.5 }} onClick={handleAddToCollection}>
              <PlaylistAddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Update status"}>
            <Button
              ref={statusButtonRef}
              variant={status === "none" ? "outlined" : "contained"}
              startIcon={
                submitting ? (
                  <CircularProgress size="1rem" color="inherit" />
                ) : (
                  currentStatus?.iconWhite
                )
              }
              endIcon={<ExpandMoreIcon />}
              onClick={handleStatusClick}
              disabled={submitting}
              sx={{
                boxShadow: "none",
                height: "38px",
                backgroundColor: currentStatus?.color, //
                color: currentStatus?.contrastText,
                "&:hover": {
                  backgroundColor: currentStatus?.color,
                },
                "&.Mui-disabled": {
                  pointerEvents: "auto",
                  backgroundColor: currentStatus?.color,
                  color: "white",
                  opacity: 1,
                },
              }}
            >
              {currentStatus?.label}
            </Button>
          </Tooltip>
        </Stack>
      </Box>
      <ListMenu
        anchorEl={statusAnchorEl}
        onClose={() => setStatusAnchorEl(null)}
        onSelect={handleStatusSelect}
        options={statusOptionsDropdown}
        footerText={statusMenuFooterText}
        align="right"
      />
      <FlagDialog
        open={openFlagDialog}
        onClose={() => {
          setOpenFlagDialog(false);
        }}
        contentId={contentId}
        contentType={contentType}
      />
      <AddToCollectionDropdown
        anchorEl={addToCollectionAnchorEl}
        contentId={contentId}
        contentType={contentType}
        onClose={() => setAddToCollectionAnchorEl(null)}
        onCreateNew={() => {
          setAddToCollectionAnchorEl(null);
          setOpenCreateCollection(true);
        }}
      />
      <CollectionDialog
        open={openCreateCollection}
        contentType={contentType}
        contentIdToAdd={contentId}
        onClose={() => setOpenCreateCollection(false)}
      />
    </>
  );
}
